import { lazy } from "react";
import { Navigate } from "react-router-dom";
import type { RouteConfig } from "../types";
import { ComponentSuspense } from "@/components/ui/suspense-wrapper";

// UTM pages
const UtmMapPage = lazy(() => import("@/pages/app/admin/settings/utm/utm-map"));
const UtmCategoriesPage = lazy(() => import("@/pages/app/admin/settings/utm/utm-categories"));
const UtmGroupsPage = lazy(() => import("@/pages/app/admin/settings/utm/utm-groups"));
const UtmZonesPage = lazy(() => import("@/pages/app/admin/settings/utm/utm-zones"));
const UtmImpactsPage = lazy(() => import("@/pages/app/admin/settings/utm/utm-impacts"));
const UtmMapSettingsPage = lazy(() => import("@/pages/app/admin/settings/utm/utm-map-settings"));

// Settings pages
const UsersSettings = lazy(() => import("@/pages/app/admin/settings/users"));
const ManagementSettings = lazy(
  () => import("@/pages/app/admin/settings/management")
);
const OrganizationChartSettings = lazy(
  () => import("@/pages/app/admin/settings/organization-chart")
);
// Roles settings pages
const RolesSettings = lazy(
  () => import("@/pages/app/admin/settings/roles")
);
// Audit logs settings page
const AuditLogsSettings = lazy(
  () => import("@/pages/app/admin/settings/audit-logs")
);
// Backup settings page
const BackupSettings = lazy(
  () => import("@/pages/app/admin/settings/backup")
);

// Dashboard management pages
const DashboardsManagement = lazy(
  () => import("@/pages/app/admin/settings/dashboards/dashboards-management")
);
const DashboardEdit = lazy(
  () => import("@/pages/app/admin/settings/dashboards/dashboard-edit")
);
const DashboardCreate = lazy(
  () => import("@/pages/app/admin/settings/dashboards/dashboard-create")
);
const WidgetCreate = lazy(
  () => import("@/pages/app/admin/settings/dashboards/widgets/widget-create")
);
const WidgetEdit = lazy(
  () => import("@/pages/app/admin/settings/dashboards/widgets/widget-edit")
);

export const adminSettingsRoutes: RouteConfig[] = [
  // UZAMaps routes (new canonical paths)
  {
    path: "admin/uzamaps/maps",
    element: (
      <ComponentSuspense>
        <UtmMapPage />
      </ComponentSuspense>
    ),
    meta: {
      title: "UTM Map | UzaForm",
      description: "Airspace management interactive map view",
    },
  },
  {
    path: "admin/uzamaps/categories/*",
    element: (
      <ComponentSuspense>
        <UtmCategoriesPage />
      </ComponentSuspense>
    ),
    meta: {
      title: "UTM Categories | UzaForm",
      description: "Manage UTM airspace categories",
    },
  },
  {
    path: "admin/uzamaps/groups/*",
    element: (
      <ComponentSuspense>
        <UtmGroupsPage />
      </ComponentSuspense>
    ),
    meta: {
      title: "UTM Groups | UzaForm",
      description: "Manage UTM airspace groups",
    },
  },
  {
    path: "admin/uzamaps/zones/*",
    element: (
      <ComponentSuspense>
        <UtmZonesPage />
      </ComponentSuspense>
    ),
    meta: {
      title: "UTM Zones | UzaForm",
      description: "Manage UTM airspace zones",
    },
  },
  {
    path: "admin/uzamaps/impacts/*",
    element: (
      <ComponentSuspense>
        <UtmImpactsPage />
      </ComponentSuspense>
    ),
    meta: {
      title: "UTM Impacts | UzaForm",
      description: "Manage UTM regulatory impacts",
    },
  },
  {
    path: "admin/uzamaps/maps-settings",
    element: (
      <ComponentSuspense>
        <UtmMapSettingsPage />
      </ComponentSuspense>
    ),
    meta: {
      title: "UTM Map Settings | UzaForm",
      description: "Configure default map center and zoom",
    },
  },
  // Legacy UTM settings routes (redirects)
  {
    path: "admin/settings/utm/map",
    element: <Navigate to="/admin/uzamaps/maps" replace />,
  },
  {
    path: "admin/settings/utm/categories/*",
    element: <Navigate to="/admin/uzamaps/categories" replace />,
  },
  {
    path: "admin/settings/utm/groups/*",
    element: <Navigate to="/admin/uzamaps/groups" replace />,
  },
  {
    path: "admin/settings/utm/zones/*",
    element: <Navigate to="/admin/uzamaps/zones" replace />,
  },
  {
    path: "admin/settings/utm/impacts/*",
    element: <Navigate to="/admin/uzamaps/impacts" replace />,
  },
  {
    path: "admin/settings/utm/maps-settings",
    element: <Navigate to="/admin/uzamaps/maps-settings" replace />,
  },
  // Settings routes
  {
    path: "admin/settings/users",
    element: (
      <ComponentSuspense>
        <UsersSettings />
      </ComponentSuspense>
    ),
    meta: {
      title: "Users Settings | UzaForm",
      description: "Manage users, roles, and user-role assignments",
    },
  },
  {
    path: "admin/settings/management",
    element: (
      <ComponentSuspense>
        <ManagementSettings />
      </ComponentSuspense>
    ),
    meta: {
      title: "Management Settings | UzaForm",
      description: "Manage system settings and configurations",
    },
  },
  {
    path: "admin/settings/organization-chart",
    element: (
      <ComponentSuspense>
        <OrganizationChartSettings />
      </ComponentSuspense>
    ),
    meta: {
      title: "Organization Chart | UzaForm",
      description: "Manage organization structure and hierarchy",
    },
  },
  {
    path: "admin/settings/roles",
    element: (
      <ComponentSuspense>
        <RolesSettings />
      </ComponentSuspense>
    ),
    meta: {
      title: "Roles Settings | UzaForm",
      description: "Manage roles and permissions",
    },
  },
  {
    path: "admin/settings/audit-logs",
    element: (
      <ComponentSuspense>
        <AuditLogsSettings />
      </ComponentSuspense>
    ),
    meta: {
      title: "Audit Logs | UzaForm",
      description: "View and manage system audit logs",
    },
  },
  {
    path: "admin/settings/backup",
    element: (
      <ComponentSuspense>
        <BackupSettings />
      </ComponentSuspense>
    ),
    meta: {
      title: "Backup | UzaForm",
      description: "Create system backups",
    },
  },
  // Dashboard management routes
  {
    path: "admin/settings/dashboard-management",
    element: (
      <ComponentSuspense>
        <DashboardsManagement />
      </ComponentSuspense>
    ),
    meta: {
      title: "Dashboards Management | UzaForm",
      description: "Manage dashboards and widgets",
    },
  },
  {
    path: "admin/settings/dashboard-management/:dashboardId/edit",
    element: (
      <ComponentSuspense>
        <DashboardEdit />
      </ComponentSuspense>
    ),
    meta: {
      title: "Dashboard Edit | UzaForm",
      description: "Edit dashboard and widgets",
    },
  },
  {
    path: "admin/settings/dashboard-management/new",
    element: (
      <ComponentSuspense>
        <DashboardCreate />
      </ComponentSuspense>
    ),
    meta: {
      title: "Dashboard Create | UzaForm",
      description: "Create new dashboard",
    },
  },
  {
    path: "admin/settings/dashboard-management/:dashboardId/widgets/new",
    element: (
      <ComponentSuspense>
        <WidgetCreate />
      </ComponentSuspense>
    ),
    meta: {
      title: "Widget Create | UzaForm",
      description: "Create new widget",
    },
  },
  {
    path: "admin/settings/dashboard-management/:dashboardId/widgets/:widgetId/edit",
    element: (
      <ComponentSuspense>
        <WidgetEdit />
      </ComponentSuspense>
    ),
    meta: {
      title: "Widget Edit | UzaForm",
      description: "Edit widget",
    },
  },
];
